import React, { useEffect, useState } from "react";
import { Box, Typography, List, ListItemButton, ListItemText, useMediaQuery, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { styled } from "@mui/material/styles";

const SidebarListItem = styled(ListItemButton)`
    border-radius: 4px;
    margin-bottom: 8px;
    padding: 12px 16px;
    transition: all 0.2s ease;
    
    &:hover {
        background-color: #fafafa;
    }

    &.active {
        background-color: #1a1a1a;
        color: white;
        
        &:hover {
            background-color: #2a2a2a;
        }
    }
`;

const StyledSelect = styled(Select)`
    font-family: 'Raleway';
    background-color: white;
    
    & .MuiOutlinedInput-notchedOutline {
        border-color: #1a1a1a;
        border-width: 1px;
    }
    
    &:hover .MuiOutlinedInput-notchedOutline {
        border-color: #1a1a1a;
        border-width: 2px;
    }
    
    &.Mui-focused .MuiOutlinedInput-notchedOutline {
        border-color: #1a1a1a;
        border-width: 2px;
    }
`;

const Articles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTag, setSelectedTag] = useState('all');
  const [availableTags, setAvailableTags] = useState([]);
  
  const isMobile = useMediaQuery('(max-width:900px)');

  useEffect(() => {
    fetch("https://v1.nocodeapi.com/meenat/medium/qnjtuQdRPqJszXEM")
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch");
        return res.json();
      })
      .then((data) => {
        setArticles(data);
        
        // Extract all unique tags from articles
        const tags = new Set();
        data.forEach(article => {
          if (Array.isArray(article.category)) {
            article.category.forEach(tag => tags.add(tag));
          }
        });
        setAvailableTags(Array.from(tags).sort());
        
        setLoading(false);
      })
      .catch((err) => {
        setError("Unable to load writings. Please check your connection and try again.");
        setLoading(false);
      });
  }, []);

  const formatDate = (timestamp) => {
    const date = new Date(Number(timestamp));
    return {
      day: date.getDate(),
      month: date.toLocaleString("en-US", { month: "short" }),
      year: date.getFullYear()
    };
  };

  const handleTagFilter = (tag) => {
    setSelectedTag(tag);
  };

  const filteredArticles = selectedTag === 'all' 
    ? articles 
    : articles.filter(article => 
        Array.isArray(article.category) && article.category.includes(selectedTag)
      );

  const SidebarContent = () => (
    <Box sx={{ width: 280, p: 3, fontFamily: 'Raleway' }}>
      <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 3, letterSpacing: '0.5px', color: '#666' }}>
        FILTER BY CATEGORY
      </Typography>
      
      <List sx={{ p: 0 }}>
        <SidebarListItem 
          className={selectedTag === 'all' ? 'active' : ''}
          onClick={() => handleTagFilter('all')}
        >
          <ListItemText 
            primary="All Writings"
            secondary={`${articles.length}`}
            primaryTypographyProps={{
              sx: { fontSize: '15px', fontWeight: selectedTag === 'all' ? 500 : 400 }
            }}
            secondaryTypographyProps={{
              sx: { 
                fontSize: '13px',
                color: selectedTag === 'all' ? 'rgba(255,255,255,0.7)' : '#999' 
              }
            }}
          />
        </SidebarListItem>
        
        {availableTags.map((tag, index) => {
          const count = articles.filter(article => 
            Array.isArray(article.category) && article.category.includes(tag)
          ).length;
          
          return (
            <SidebarListItem
              key={index}
              className={selectedTag === tag ? 'active' : ''}
              onClick={() => handleTagFilter(tag)}
            >
              <ListItemText 
                primary={tag}
                secondary={`${count}`}
                primaryTypographyProps={{
                  sx: { fontSize: '15px', fontWeight: selectedTag === tag ? 500 : 400 }
                }}
                secondaryTypographyProps={{
                  sx: { 
                    fontSize: '13px',
                    color: selectedTag === tag ? 'rgba(255,255,255,0.7)' : '#999' 
                  }
                }}
              />
            </SidebarListItem>
          );
        })}
      </List>
    </Box>
  );

  if (loading) {
    return (
      <div
        style={{
          maxWidth: "1100px",
          margin: "0 auto",
          padding: "80px 24px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          fontFamily: "Raleway",
        }}
      >
        <div
          style={{
            width: "48px",
            height: "48px",
            border: "4px solid #f0f0f0",
            borderTop: "4px solid #1a1a1a",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            marginBottom: "16px"
          }}
        />
        <p style={{ color: "#666", fontSize: "16px", margin: 0, fontFamily: "Raleway" }}>
          This would only take a few seconds...
        </p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          maxWidth: "1100px",
          margin: "0 auto",
          padding: "80px 24px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          fontFamily: 'Raleway'
        }}
      >
        <p
          style={{
            fontSize: "18px",
            color: "#d32f2f",
            marginBottom: "16px",
            textAlign: "center"
          }}
        >
          {error}
        </p>
        <p
          style={{
            fontSize: "14px",
            color: "#666",
            textAlign: "center",
            cursor: "pointer",
            textDecoration: "underline",
            margin: 0
          }}
          onClick={() => window.location.reload()}
          onMouseEnter={(e) => (e.target.style.color = "#1a1a1a")}
          onMouseLeave={(e) => (e.target.style.color = "#666")}
        >
          Click here to retry
        </p>
      </div>
    );
  }

  return (
    <Box sx={{ display: 'flex', fontFamily: "Raleway" }}>
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Box
          sx={{
            width: 280,
            flexShrink: 0,
            borderRight: '1px solid #e0e0e0',
            position: 'sticky',
            top: 80,
            height: 'calc(100vh - 80px)',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          <SidebarContent />
        </Box>
      )}

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          maxWidth: isMobile ? "100%" : "1100px",
          margin: "0 auto",
          padding: "80px 24px",
          color: "#1a1a1a"
        }}
      >
        {/* Header */}
        <Typography sx={{ fontSize: "28px", fontWeight: 500, mb: 1 }}>
          My Writings
        </Typography>

        <Typography sx={{ color: "#666", mb: 4, fontSize: "20px" }}>
          I have realized that I write and publish as articles mainly to document important things I'm learning, so I can revisit them later. I try to explain concepts in my own words and often include additional research I've done while learning them, making it easier for me to review in the future.
          <p> It's also a bonus if you(others) find them helpful. You can also follow me on <a href="https://medium.com/@meenatt"> Medium </a> &#10084;&#65039; </p>
        </Typography>

        {/* Mobile Dropdown Filter */}
        {isMobile && (
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel 
              id="tag-filter-label" 
              sx={{ 
                fontFamily: 'Raleway',
                color: '#1a1a1a',
                '&.Mui-focused': {
                  color: '#1a1a1a'
                }
              }}
            >
              Filter by Category
            </InputLabel>
            <StyledSelect
              labelId="tag-filter-label"
              value={selectedTag}
              label="Filter by Category"
              onChange={(e) => handleTagFilter(e.target.value)}
            >
              <MenuItem value="all" sx={{ fontFamily: 'Raleway' }}>
                All Writings ({articles.length})
              </MenuItem>
              {availableTags.map((tag, index) => {
                const count = articles.filter(article => 
                  Array.isArray(article.category) && article.category.includes(tag)
                ).length;
                
                return (
                  <MenuItem key={index} value={tag} sx={{ fontFamily: 'Raleway' }}>
                    <b> {tag} </b> ({count})
                  </MenuItem>
                );
              })}
            </StyledSelect>
          </FormControl>
        )}

        
        {/* Articles */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "28px" }}>
          {filteredArticles.map((item, index) => {
            const { day, month, year } = formatDate(item.created);

            return (
              <a
                key={index}
                href={item.link}
                target="_blank"
                rel="noreferrer"
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    border: "1px solid #e0e0e0",
                    backgroundColor: "#fff",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: "black",
                      transform: "translateX(4px)",
                      border: "2px solid"
                    },
                    "&:hover .article-title": {
                      color: "#000"
                    }
                  }}
                >
                  {/* Date column */}
                  <Box
                    sx={{
                      width: "96px",
                      backgroundColor: "#f2f2f2",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0
                    }}
                  >
                    <Typography sx={{ fontSize: "22px", fontWeight: 600 }}>
                      {day}
                    </Typography>
                    <Typography sx={{ fontSize: "13px", color: "#555" }}>
                      {month}, {year}
                    </Typography>
                  </Box>

                  {/* Content */}
                  <Box sx={{ padding: "28px 32px" }}>
                    <Typography
                      className="article-title"
                      sx={{
                        fontSize: "20px",
                        fontWeight: 500,
                        lineHeight: 1.45,
                        color: "#1a1a1a",
                        transition: "color 0.2s ease",
                        mb: 1
                      }}
                    >
                      {item.title}
                    </Typography>

                    {/* Categories (quiet metadata) */}
                    {Array.isArray(item.category) && (
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#777",
                          letterSpacing: "0.3px"
                        }}
                      >
                        {item.category.join(" · ")}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </a>
            );
          })}
        </Box>
<br/>
        {/* Articles Count */}
        <Typography sx={{ color: "#666", mb: 3, fontSize: "16px" }}>
          <b>Showing {filteredArticles.length} {filteredArticles.length === 1 ? 'writing' : 'writings'}
          {selectedTag !== 'all' && ` in "${selectedTag}"`}</b>
        </Typography>


        {/* No results message */}
        {filteredArticles.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography sx={{ fontSize: "18px", color: "#666" }}>
              No articles found for this category.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Articles;